import { useState, createContext, useContext } from 'react'

type ShopPageState = {
  isSearching: boolean
  searchValue: string
  setSearchValue: (value: string) => void
  isAutoScrolling: boolean
  handleAutoScrolling: () => void
  shouldShowAllCategories: boolean
  setShouldShowAllCategories: (value: boolean) => void
  productErrorOnContinue: ((proceedWithNewOrderItems?: boolean) => void) | null
  setProductErrorOnContinue: React.Dispatch<
    React.SetStateAction<((proceedWithNewOrderItems?: boolean) => void) | null>
  >
  shouldReloadProducts: boolean
  setShouldReloadProducts: (value: boolean) => void
  isShopSearchVisible: boolean
  setIsShopSearchVisible: (value: boolean) => void
}

export const SetupShopPageStore = (): ShopPageState => {
  const [isAutoScrolling, setIsAutoScrolling] = useState(false)
  const [autoScrollTimeout, setAutoScrollTimeout] = useState<null | ReturnType<
    typeof setTimeout
  >>(null)
  const [searchValue, setSearchValue] = useState('')
  const [shouldShowAllCategories, setShouldShowAllCategories] = useState(false)
  const [productErrorOnContinue, setProductErrorOnContinue] = useState<
    ((proceedWithNewOrderItems?: boolean) => void) | null
  >(null)
  const [shouldReloadProducts, setShouldReloadProducts] = useState(false)
  const [isShopSearchVisible, setIsShopSearchVisible] = useState(false)

  const handleAutoScrolling = () => {
    if (autoScrollTimeout) {
      clearTimeout(autoScrollTimeout)
    }

    setIsAutoScrolling(true)

    const scrollDuration = 700
    const timeout = setTimeout(() => setIsAutoScrolling(false), scrollDuration)
    setAutoScrollTimeout(timeout)
  }

  const isSearching = searchValue.length >= 3

  return {
    isSearching,
    searchValue,
    setSearchValue,
    isAutoScrolling,
    handleAutoScrolling,
    shouldShowAllCategories,
    setShouldShowAllCategories,
    productErrorOnContinue,
    setProductErrorOnContinue,
    shouldReloadProducts,
    setShouldReloadProducts,
    isShopSearchVisible,
    setIsShopSearchVisible
  }
}

const initialState: ShopPageState = {
  isSearching: false,
  searchValue: '',
  setSearchValue: () => {},
  isAutoScrolling: false,
  handleAutoScrolling: () => {},
  shouldShowAllCategories: false,
  setShouldShowAllCategories: () => {},
  productErrorOnContinue: null,
  setProductErrorOnContinue: () => {},
  shouldReloadProducts: false,
  setShouldReloadProducts: () => {},
  isShopSearchVisible: false,
  setIsShopSearchVisible: () => {}
}

export const ShopPageContext = createContext<ShopPageState>(initialState)

export const useShopPage = () => {
  return useContext(ShopPageContext)
}
